import React from 'react';
import { Pie, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Tooltip, Legend, Filler } from 'chart.js';
import type { AADistributionResponse } from '../../../types/smart-router';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Tooltip, Legend, Filler);

interface Props { data: AADistributionResponse; }

const AA_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export const TrafficDistributionSection: React.FC<Props> = ({ data }) => {
  const distributions = data?.distributions ?? [];
  const daily_trend = data?.daily_trend ?? [];

  const pieData = {
    labels: distributions.map(d => d.aa_id),
    datasets: [{ data: distributions.map(d => d.total_consents), backgroundColor: AA_COLORS.slice(0, distributions.length), borderWidth: 2, borderColor: '#fff', cutout: '30%' }],
  };
  const pieOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx: any) => { const v = ctx.parsed; return `${ctx.label}: ${v.toLocaleString()} (${((v / (data?.total || 1)) * 100).toFixed(1)}%)`; } } } },
  };

  const aaIds = distributions.map(d => d.aa_id);
  const lineData = {
    labels: daily_trend.map(t => { const d = new Date(t.date); return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }),
    datasets: aaIds.map((aaId, i) => ({
      label: aaId,
      data: daily_trend.map(t => t.aa_breakdown.find(b => b.aa_id === aaId)?.total_consents ?? 0),
      borderColor: AA_COLORS[i], backgroundColor: `${AA_COLORS[i]}18`, fill: true, tension: 0.4, pointRadius: 2,
    })),
  };
  const lineOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { labels: { color: '#6b7280' } } },
    scales: { y: { beginAtZero: true, grid: { color: '#f3f4f6' } }, x: { grid: { color: '#f3f4f6' } } },
  };

  return (
    <div className="border border-border-default rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-b from-primary-50 to-primary-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-text-primary tracking-wider">Traffic Distribution to Account Aggregators</h2>
      </div>
      <div className="bg-white p-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex items-center gap-6">
            <div className="w-48 h-48"><Pie data={pieData} options={pieOptions} /></div>
            <div className="flex flex-col gap-3">
              {distributions.map((d, i) => (
                <div key={d.aa_id} className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: AA_COLORS[i] }} />
                  <span className="text-sm text-text-secondary">{d.aa_id}</span>
                  <span className="text-sm font-semibold text-text-primary">{d.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="h-48"><Line data={lineData} options={lineOptions} /></div>
        </div>
      </div>
    </div>
  );
};
